/**
 * Type guard function to check if a value is defined (not null and not undefined).
 *
 * This function serves as a type predicate, allowing TypeScript to narrow the type
 * of the checked value in conditional blocks.
 *
 * @template T The type of the value being checked.
 * @param {T | null | undefined} value The value to check for definedness.
 * @returns {boolean} True if the value is neither null nor undefined, false otherwise.
 *
 * @example
 * // Basic usage
 * const maybeString: string | null | undefined = getSomeValue();
 * if (isDefined(maybeString)) {
 *     // TypeScript knows maybeString is string here
 *     console.log(maybeString.toUpperCase());
 * }
 *
 * @remarks
 * - It helps improve type safety by allowing TypeScript to infer more specific types
 *   after the check.
 * - The function does not distinguish between null and undefined; it treats both as "not defined".
 */
export const isDefined = <T>(
    value: T | null | undefined,
): value is Exclude<T, null | undefined> => {
    return value !== undefined && value !== null;
};
